import { type components } from "@njbsoft/types/field";
import { type paths as fieldPaths } from "@njbsoft/types/field";
import { ApiError } from "./api-error";
import { buildClient, type ApiClientOptions } from "./client";

// Typed openapi-fetch client for the /api/v1/field stack. Same auth
// middleware as the main client — the same personal bearer works on both
// stacks because field context is DB-derived per request. Field paths carry
// no /auth/ segment, so the AUTH_PATHS skip-list never short-circuits and
// the 401 refresh-replay applies to every field request.
export function createFieldApiClient(options: ApiClientOptions) {
  return buildClient<fieldPaths>(options);
}

export type FieldApiClient = ReturnType<typeof createFieldApiClient>;

export type MyUtilityAccessRequest = components["schemas"]["MyUtilityAccessRequest"];

/**
 * Ask one utility for a personal access authorization — the roster member's
 * door onto the grant mint. Names the ORG; the caller's live company (and
 * its live edge with that utility) is resolved server-side and vouches for
 * the request. Throws typed on every declared refusal:
 * 403 `email_not_verified`, 404 `utility_not_requestable`,
 * 409 `access_request_exists` / `access_request_terminal`.
 */
export async function createMyUtilityAccessRequest(
  client: FieldApiClient,
  orgId: string,
): Promise<MyUtilityAccessRequest> {
  const { data, error, response } = await client.POST("/utility-access-requests", {
    body: { org_id: orgId },
  });
  if (error) {
    // The union narrows to the declared 409 envelope on this arm, but the
    // remaining declared bodies (400/401/403/404/500) all arrive as
    // `{ error: string }` — openapi-fetch types the arm union loosely, so
    // the shared fields are read by hand.
    const envelope = error as { error?: string; code?: string; message?: string };
    throw new ApiError(
      response.status,
      envelope.error ?? envelope.code ?? "Failed to request utility access",
      envelope.code ?? envelope.error,
      error,
    );
  }
  return data;
}
